import { useEffect, useState } from "react";
import {
  BarChart3,
  TrendingUp,
  Eye,
  Users,
  Calendar,
  Layers,
  ArrowUpRight,
  ExternalLink,
  Flame,
  Award
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { LoadingState, EmptyState, ErrorState } from "../../components/LoadingState";
import { apiGet } from "../../services/api";
import type { TrafficOverviewData, Dashboard } from "../../types";

export function AnalyticsPage() {
  const [days, setDays] = useState<number>(30);
  const [trafficData, setTrafficData] = useState<TrafficOverviewData | null>(null);
  const [dashboardData, setDashboardData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadAnalytics(selectedDays: number) {
    try {
      setLoading(true);
      setError(null);
      const [trafficRes, dashRes] = await Promise.all([
        apiGet<TrafficOverviewData>(`/api/admin/analytics/traffic?days=${selectedDays}`),
        apiGet<Dashboard>("/api/admin/dashboard")
      ]);
      setTrafficData(trafficRes);
      setDashboardData(dashRes);
    } catch (err: any) {
      setError(err.message || "Failed to load publication analytics");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAnalytics(days);
  }, [days]);

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <BarChart3 size={13} className="text-[#C99A2E]" />
            <span>AUDIENCE INTELLIGENCE</span>
          </div>
          <h1 className="admin-title">Readership & Performance Analytics</h1>
          <p className="admin-subtitle">
            Track reader traffic, article impressions, and editorial engagement across the ZimSmart network.
          </p>
        </div>

        {/* Timeframe selector */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                days === d
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Last {d} Days
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <LoadingState label="Synthesizing audience telemetry..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <>
          {/* KPI Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="admin-metric-card">
              <div className="flex items-center justify-between">
                <span className="admin-metric-label">Total Views ({days}d)</span>
                <Eye size={18} className="text-sky-600" />
              </div>
              <div className="admin-metric-value">
                {trafficData?.totalViews.toLocaleString() ?? 0}
              </div>
              <p className="admin-metric-note">Article page impressions</p>
            </div>

            <div className="admin-metric-card">
              <div className="flex items-center justify-between">
                <span className="admin-metric-label">Unique Readers ({days}d)</span>
                <Users size={18} className="text-emerald-600" />
              </div>
              <div className="admin-metric-value">
                {trafficData?.totalVisitors.toLocaleString() ?? 0}
              </div>
              <p className="admin-metric-note">Distinct readers</p>
            </div>

            <div className="admin-metric-card">
              <div className="flex items-center justify-between">
                <span className="admin-metric-label">Daily Average</span>
                <TrendingUp size={18} className="text-[#C99A2E]" />
              </div>
              <div className="admin-metric-value">
                {trafficData?.dailyAverage.toLocaleString() ?? 0}
              </div>
              <p className="admin-metric-note">Views per day</p>
            </div>

            <div className="admin-metric-card">
              <div className="flex items-center justify-between">
                <span className="admin-metric-label">Published Articles</span>
                <Layers size={18} className="text-purple-600" />
              </div>
              <div className="admin-metric-value">
                {dashboardData?.publishing?.published_total ?? 0}
              </div>
              <p className="admin-metric-note">Active live stories</p>
            </div>
          </div>

          {/* Traffic Chart */}
          <div className="admin-card-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                  Traffic Trajectory & Daily Impressions
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Visual breakdown of article views and unique visitors over the last {days} days
                </p>
              </div>
            </div>

            <div className="h-72 w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={trafficData?.history ?? []}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C99A2E" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#C99A2E" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#111318",
                      borderColor: "#334155",
                      borderRadius: "8px",
                      color: "#fff",
                      fontSize: "12px"
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name="Article Views"
                    stroke="#C99A2E"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#viewsGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="visitors"
                    name="Unique Readers"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#visitorsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom Grid: Most Read & Categories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Stories */}
            <div className="admin-card-panel">
              <div className="flex items-center gap-2 mb-4">
                <Flame size={16} className="text-amber-500" />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Most Read Editorial Stories
                </h3>
              </div>

              <div className="flex flex-col divide-y divide-slate-100">
                {dashboardData?.performance?.mostRead?.map((item, idx) => (
                  <div key={item.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <a
                        href={item.slug ? `/article/${item.slug}` : `/admin/articles/${item.id}/edit`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-slate-900 hover:text-[#C99A2E] line-clamp-1 flex items-center gap-1"
                      >
                        <span>{item.title}</span>
                        <ExternalLink size={11} className="text-slate-400 shrink-0" />
                      </a>
                    </div>

                    <span className="text-xs font-bold text-slate-700 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-200 shrink-0">
                      {item.views.toLocaleString()} views
                    </span>
                  </div>
                ))}

                {(!dashboardData?.performance?.mostRead || dashboardData.performance.mostRead.length === 0) && (
                  <p className="text-xs text-slate-400 py-4 text-center">No views logged yet.</p>
                )}
              </div>
            </div>

            {/* Top Categories */}
            <div className="admin-card-panel">
              <div className="flex items-center gap-2 mb-4">
                <Award size={16} className="text-[#C99A2E]" />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Coverage by Editorial Section
                </h3>
              </div>

              <div className="flex flex-col divide-y divide-slate-100">
                {dashboardData?.editorial?.topCategories?.map((cat) => (
                  <div key={cat.name} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#C99A2E]" />
                      <span className="text-xs font-semibold text-slate-800">{cat.name}</span>
                    </div>
                    <span className="text-xs font-mono text-slate-500">
                      {cat.articles} article{cat.articles === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
